<template>
  <SubHeading :home="home" :tabs="tabs" :currTab="currTab" @active="handleActive" />

  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <router-link to="/tariff-mini" class="breadcrumb sba-collection-crumb blue-grey-text text-darken-3">
          <span>Tariff Mini Home</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text pointable">
          <span>View</span>
        </span>
        <span class="sba-collection-context">Tariff Adjustment Workspace</span>
      </div>
    </div>
  </nav>

  <div id="top"></div>

  <div class="sba-collection-page-surface is-workbench tariff-mini-secondary-surface">
    <div class="container">
      <LoadingDiv v-if="!isReady" />

      <div class="tariff-mini-workbench sba-slide-in" v-else>
        <section class="tariff-mini-secondary-header">
          <section class="tariff-mini-super-card">
            <button type="button" class="secondary-page-back" title="Back to All Tariff Mini"
              @click.prevent="handleActive(home)">
              <i class="material-icons">keyboard_return</i>
            </button>
            <div class="tariff-mini-super-copy">
              <button type="button" class="secondary-parent-link" @click.prevent="handleActive(home)">
                All Tariff Mini
              </button>
              <small>Back to provider tariff list</small>
            </div>
          </section>

          <section class="tariff-mini-dossier-card">
            <div class="tariff-mini-avatar" aria-hidden="true">
              {{ providerInitials }}
            </div>
            <div class="tariff-mini-dossier-title">
              <label class="active teal-text">Tariff Mini</label>
              <h5>{{ capWords(provider?.name || "Provider") }}</h5>
              <p>Review existing tariff items and adjust agreed provider prices.</p>
            </div>

            <div class="tariff-mini-dossier-context">
              <span class="context-pill" :class="providerStatusClass">{{ providerStatusLabel }}</span>
              <span class="context-pill">{{ provider?.state || "State not recorded" }}</span>
              <span class="context-pill">{{ provider?.phone || "No phone recorded" }}</span>
            </div>
          </section>
        </section>

        <section class="tariff-mini-secondary-body-frame">
          <TariffMiniDiv :provider="provider" />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItem from "@/composables/getItem";
import { capWordsFx } from "@/composables/fxLibrary/coreFx.js";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import TariffMiniDiv from "@/components/tariffMini/TariffMiniComponent.vue";

export default {
  name: "tariffMiniView",
  components: { SubHeading, LoadingDiv, TariffMiniDiv },
  props: ["id"],
  setup(props) {
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);

    const { routeToTab } = useModuleNavigation();

    onMounted(() => {
      document.title = "Tariff Mini | EasyHMO";
    });

    onMounted(() => {
      tabs.value.push({ id: 1, name: "Tariff Mini", title: "All", icon: "arrow_back", url: "tariffMini", hash: null, });
      tabs.value.push({ id: 2, name: "Tariff Mini", title: "View", icon: null, url: "tariffMini", hash: "#view", });

      currTab.value = tabs.value.find(x => x.title.includes("View"));
      home.value = tabs.value.find(x => x.id === 1);
    });

    const handleActive = (tab) => {
      routeToTab(tab, { replace: !tab?.url });
    };

    const capWords = (val) => {
      return capWordsFx(val);
    };

    const getProvider = () => {
      (async () => {
        const { res, err } = await getItem("provider", props.id);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        isReady.value = true;
      })();
    };

    onMounted(() => {
      getProvider();
    });

    const provider = computed(() => {
      if (!response.value) {
        return {};
      } else {
        return response.value;
      }
    });

    const providerInitials = computed(() => {
      const name = provider.value?.name || "Provider";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((x) => x.charAt(0))
        .join("")
        .toUpperCase();
    });

    const providerStatusLabel = computed(() => {
      if (provider.value?.status === "s") {
        return "Inactive provider";
      }

      if (provider.value?.status === "p") {
        return "Pending approval";
      }

      return "Active provider";
    });

    const providerStatusClass = computed(() => {
      if (provider.value?.status === "s") {
        return "is-inactive";
      }

      if (provider.value?.status === "p") {
        return "is-pending";
      }

      return "is-active";
    });

    return {
      handleActive,
      home,
      currTab,
      tabs,
      capWords,
      error,
      isReady,
      provider,
      providerInitials,
      providerStatusClass,
      providerStatusLabel,
    };
  },
};
</script>

<style scoped>
.tariff-mini-secondary-surface {
  min-height: calc(100vh - 260px);
  padding-bottom: 56px;
}

.tariff-mini-workbench {
  color: #263238;
  padding-bottom: 48px;
  padding-top: 0;
  position: relative;
  width: 100%;
  z-index: 1;
}

.tariff-mini-super-card,
.tariff-mini-dossier-card,
.tariff-mini-secondary-body-frame,
.tariff-mini-workbench :deep(.tariff-mini-card),
.tariff-mini-workbench :deep(.tariff-mini-search-card),
.tariff-mini-workbench :deep(.tariff-mini-row),
.tariff-mini-workbench :deep(.tariff-mini-empty) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.tariff-mini-secondary-header {
  box-shadow: 0 20px 34px -32px rgba(38, 50, 56, 0.82);
  margin-bottom: 0;
  overflow: visible;
  position: relative;
}

.tariff-mini-super-card {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  display: flex;
  gap: 10px;
  min-height: 46px;
  padding: 10px 14px;
  position: relative;
  width: 100%;
  z-index: 3;
}

.tariff-mini-super-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.15;
}

.secondary-page-back {
  align-items: center;
  background: #00897b;
  border: 1px solid #00796b;
  border-radius: 5px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 24px;
}

.secondary-page-back:hover {
  background: #00695c;
  border-color: #00695c;
}

.secondary-page-back i {
  color: #ffffff;
  font-size: 16px;
  line-height: 16px;
}

.secondary-parent-link {
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #4e342e;
  cursor: pointer;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 800;
  margin: 0;
  padding: 0;
  text-align: left;
  text-transform: uppercase;
  transition: color 0.18s ease;
}

.secondary-parent-link:hover {
  color: #00897b;
}

.tariff-mini-super-copy small {
  color: #6d4c41;
  font-size: 0.72rem;
  font-weight: 700;
}

.tariff-mini-dossier-card {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  display: grid;
  gap: 22px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 26px 34px;
  position: relative;
}

.tariff-mini-avatar {
  align-items: center;
  background: #e0f2f1;
  border: 1px solid #80cbc4;
  border-radius: 50%;
  color: #00695c;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 1.25rem;
  font-weight: 800;
  height: 58px;
  justify-content: center;
  width: 58px;
}

.tariff-mini-dossier-title {
  min-width: 0;
}

.tariff-mini-dossier-title label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.tariff-mini-dossier-title h5 {
  color: #263238;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 8px;
  overflow-wrap: anywhere;
}

.tariff-mini-dossier-title p {
  color: #607d8b;
  font-size: 1rem;
  line-height: 1.55;
  margin: 0;
  max-width: 760px;
}

.tariff-mini-dossier-context {
  align-items: flex-end;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  max-width: 430px;
}

.context-pill {
  background: #fafafa;
  border: 1px solid #e1e8eb;
  border-radius: 999px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 11px;
}

.context-pill.is-active {
  background: #e8f3f2;
  border-color: #9bd2cb;
  color: #00695c;
}

.context-pill.is-inactive {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.context-pill.is-pending {
  background: #fff8e1;
  border-color: #ffe082;
  color: #795548;
}

.tariff-mini-secondary-body-frame {
  background: #f5f7f8;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  box-sizing: border-box;
  margin-top: 14px;
  padding: 16px;
  position: relative;
}

.tariff-mini-secondary-body-frame :deep(.tariff-mini-card),
.tariff-mini-secondary-body-frame :deep(.tariff-mini-row),
.tariff-mini-secondary-body-frame :deep(.tariff-mini-empty) {
  border-color: #c4d3d9 !important;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72) !important;
}

.tariff-mini-secondary-body-frame :deep(.tariff-mini-row:hover) {
  border-color: #80cbc4 !important;
  box-shadow: 0 16px 26px -26px rgba(0, 105, 92, 0.62) !important;
}

.pointable {
  cursor: default;
}

@media only screen and (max-width: 992px) {
  .tariff-mini-dossier-card {
    align-items: center;
    grid-template-columns: auto minmax(0, 1fr);
  }

  .tariff-mini-dossier-context {
    align-items: flex-start;
    grid-column: 1 / -1;
    justify-content: flex-start;
    max-width: none;
    width: 100%;
  }
}

@media only screen and (max-width: 600px) {
  .tariff-mini-dossier-card,
  .tariff-mini-secondary-body-frame {
    padding: 20px;
  }

  .tariff-mini-dossier-card {
    gap: 14px;
  }

  .tariff-mini-avatar {
    font-size: 1.05rem;
    height: 48px;
    width: 48px;
  }

  .tariff-mini-dossier-title h5 {
    font-size: 1.35rem;
    margin-bottom: 6px;
  }

  .tariff-mini-dossier-title p {
    font-size: 0.94rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tariff-mini-super-card,
  .tariff-mini-dossier-card,
  .tariff-mini-secondary-body-frame,
  .tariff-mini-workbench :deep(.tariff-mini-card),
  .tariff-mini-workbench :deep(.tariff-mini-search-card),
  .tariff-mini-workbench :deep(.tariff-mini-row),
  .tariff-mini-workbench :deep(.tariff-mini-empty) {
    animation: none;
  }
}
</style>
